/*
 * Phase 4A Commit 4Aa — wholesale-apply.js
 *
 * 3-step form state machine for the wholesale application page.
 * Posts to /apps/dashboard/wholesale/apply via Shopify App Proxy.
 *
 * Step transitions: Next button validates the current step's required
 * fields client-side, then advances. Back unconditionally rewinds.
 *
 * Product decision (post-2d5bc1b hotfix): the v1 form ships WITHOUT
 * a resale-cert upload step. v1 verification is admin manual review;
 * the cert was one signal of many. The cert upload code (signed-URL
 * fetch + direct browser PUT to Supabase Storage) is gone from this
 * file but the backend endpoints + storage helper are retained for
 * use in 4Af (tax-exempt cert upload, where the customer is already
 * approved and has motivation to dig up paperwork) + future v2
 * re-add.
 *
 * Vanilla ES5+ (no React / jQuery) to match the existing
 * customer-dashboard.js convention. No build step required.
 */
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  var root = $('[data-wholesale-apply]');
  if (!root) return;

  var form = $('[data-wholesale-form]', root);
  var stepsNav = $('[data-wholesale-steps]', root);
  var errorEl = $('[data-wholesale-error]', root);
  var successEl = $('[data-wholesale-success]', root);

  var currentStep = 1;
  var totalSteps = 3;

  // ─── Step navigation ─────────────────────────────────────────────────

  function showStep(n) {
    $$('.wholesale-step', form).forEach(function (fs) {
      fs.hidden = String(fs.getAttribute('data-wholesale-step')) !== String(n);
    });
    if (stepsNav) {
      $$('li', stepsNav).forEach(function (li) {
        var step = Number(li.getAttribute('data-step'));
        li.classList.toggle('is-active', step === n);
        li.classList.toggle('is-complete', step < n);
      });
    }
    currentStep = n;
    hideError();
    // No scrollIntoView on step transitions — the form is short
    // enough that scroll-management isn't needed, and stepping
    // through the form would otherwise shift the page on every
    // Next/Back click. The error-region + success-state
    // scrollIntoView calls remain (those ARE meaningful
    // transitions).
  }

  function validateStep(n) {
    var fs = form.querySelector('.wholesale-step[data-wholesale-step="' + n + '"]');
    if (!fs) return true;
    var inputs = $$('input,select,textarea', fs);
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      if (!el.willValidate || el.disabled) continue;
      if (!el.checkValidity()) {
        el.reportValidity();
        return false;
      }
    }
    return true;
  }

  function hideError() {
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
  }

  function showError(msg) {
    if (!errorEl) return;
    errorEl.hidden = false;
    errorEl.textContent = msg;
    errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  $$('[data-wholesale-next]', form).forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (!validateStep(currentStep)) return;
      if (currentStep < totalSteps) showStep(currentStep + 1);
    });
  });

  $$('[data-wholesale-back]', form).forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (currentStep > 1) showStep(currentStep - 1);
    });
  });

  // ─── Submit ──────────────────────────────────────────────────────────

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    // The form is novalidate and the final step's default button is type=submit,
    // so pressing Enter in a step-1/2 field implicit-submits with later required
    // fields still null. Before the final step, treat submit as Next: validate the
    // current step and advance rather than posting.
    if (currentStep < totalSteps) {
      if (validateStep(currentStep)) showStep(currentStep + 1);
      return;
    }
    // Final step: validate EVERY step before posting (reveal the first failing
    // step so reportValidity can anchor its message to a visible field).
    for (var s = 1; s <= totalSteps; s++) {
      showStep(s);
      if (!validateStep(s)) return;
    }
    var fd = new FormData(form);

    var body = {
      legal_business_name: fd.get('legal_business_name'),
      display_name: fd.get('display_name'),
      business_address: {
        line1: fd.get('address_line1'),
        line2: fd.get('address_line2') || null,
        city: fd.get('city'),
        province: fd.get('province'),
        postal_code: fd.get('postal_code'),
        country: 'Canada',
      },
      business_type: fd.get('business_type'),
      gst_number: fd.get('gst_number') || null,
      expected_monthly_volume: fd.get('expected_monthly_volume') || null,
      buyer_contact: {
        name: fd.get('buyer_name'),
        email: fd.get('buyer_email'),
        phone: fd.get('buyer_phone') || null,
      },
      shop_description: fd.get('shop_description') || null,
      // Parse trade_references textarea into a single-string array
      // (form-data sends it as one blob; backend stores as jsonb).
      trade_references: fd.get('trade_references')
        ? [fd.get('trade_references')]
        : [],
      marketing_consent: fd.get('marketing_consent') === 'on',
    };

    hideError();
    var submitBtn = $('[data-wholesale-submit]', form);
    if (submitBtn) submitBtn.disabled = true;

    // Invisible bot checks (circadia-form-guard.js): a hidden field and a signed page-load timestamp ride along; nothing is shown to the visitor.
    new Promise(function (resolve) {
      if (window.CircadiaFormGuard) { window.CircadiaFormGuard.withGuard(form, body, resolve); } else { resolve(body); }
    })
      .then(function (guardedBody) {
        return fetch('/apps/dashboard/wholesale/apply', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(guardedBody),
        });
      })
      .then(function (r) {
        return r.json().then(function (data) {
          return { ok: r.ok, status: r.status, data: data };
        });
      })
      .then(function (result) {
        if (result.ok) {
          if (successEl) {
            form.hidden = true;
            if (stepsNav) stepsNav.hidden = true;
            successEl.hidden = false;
            successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
        if (result.status === 409 && result.data && result.data.code === 'reapply_lockout') {
          showError(result.data.error || 'A prior application from this email was declined.');
        } else {
          var msg = (result.data && result.data.error) || 'Submission failed. Please try again.';
          showError(msg);
        }
      })
      .catch(function (err) {
        showError('Network error: ' + (err && err.message ? err.message : 'try again') + '.');
      })
      .then(function () {
        if (submitBtn) submitBtn.disabled = false;
      });
  });
})();
